import { useEffect } from 'react';
import smartlookClient from 'smartlook-client';
import { getRuntimeConfig } from 'lib/runtimeConfig';

import {
  userNamesSelector,
  userGUIDSelector,
  userIdSelector,
  isBetaUserSelector,
  isSmartlookRecordingSelector,
} from 'domain/env';
import { currentConnectedERPselector } from 'domain/settings/settingsSelector';
import { useSelector } from 'react-redux';

const SmartLook: React.FC = () => {
  const userId = useSelector(userGUIDSelector);
  const userEmail = useSelector(userIdSelector);
  const userName = useSelector(userNamesSelector);
  const isBetaUser = useSelector(isBetaUserSelector);
  const isSmartlookRecording = useSelector(isSmartlookRecordingSelector);
  const erpType = useSelector(currentConnectedERPselector);

  // Initialize Smartlook and configure recording
  useEffect(() => {
    // Don't initialize Smartlook at all if recording is disabled
    if (!isSmartlookRecording) {
      return;
    }

    document.body.dataset.sl = 'canvas-mq';

    const appId = (getRuntimeConfig().env as Record<string, string>).REACT_APP_SMART_LOOK_ID;
    if (!appId) {
      return;
    }

    smartlookClient.init(appId);

    // Configure what to record (forms, emails, etc)
    smartlookClient.record({ forms: true, numbers: true, emails: true, ips: true });
  }, [isSmartlookRecording]);

  // Identify user (runs on user data change)
  useEffect(() => {
    if (!isSmartlookRecording) {
      return;
    }

    smartlookClient.identify(userId, {
      name: `${userName.firstName} ${userName.lastName}`,
      email: userEmail,
      isBetaUser,
    });
  }, [userId, userName.firstName, userName.lastName, userEmail, isBetaUser, isSmartlookRecording]);

  // Track ERP type as a custom event so sessions can be filtered by ERP name
  useEffect(() => {
    if (!isSmartlookRecording || !erpType) {
      return;
    }

    smartlookClient.track(`erp_${erpType}`, { erpType });
  }, [isSmartlookRecording, erpType]);

  return null;
};

SmartLook.displayName = 'SmartLook';

export default SmartLook;
